import {
  APPOINTMENT_COLOR_BACKGROUND_EXTENSION,
  APPOINTMENT_COLOR_BORDER_EXTENSION,
  APPOINTMENT_COLOR_TEXT_EXTENSION,
} from '@bariendo/lib/extensions';
import { ActivityDefinition, Appointment, Extension } from '@medplum/fhirtypes';

/**
 * Default color for appointment types without ActivityDefinition extensions
 */
const DEFAULT_COLOR = {
  backgroundColor: '#9DB4C1',
  borderColor: '#7A9BA8',
  textColor: '#FFFFFF',
};

/**
 * Gets the color configuration for an appointment
 * Colors are read from ActivityDefinition extensions, which are looked up based on the Appointment's appointmentType code
 * @param appointment - Appointment resource with appointmentType field
 * @param getActivityDefinition - Function to look up ActivityDefinition by appointment type code (for mocking/testing)
 * @returns Color configuration object (defaults to gray if extensions not found)
 */
export function getAppointmentColor(
  appointment: Appointment,
  getActivityDefinition?: (code: string | undefined) => ActivityDefinition | undefined
): {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
} {
  if (!getActivityDefinition) {
    return DEFAULT_COLOR;
  }

  const appointmentTypeCode = appointment.appointmentType?.coding?.[0]?.code;
  const activityDefinition = getActivityDefinition(appointmentTypeCode);

  return getActivityDefinitionColor(activityDefinition);
}

export function getActivityDefinitionColor(activityDefinition?: ActivityDefinition) {
  if (!activityDefinition?.extension) {
    return DEFAULT_COLOR;
  }

  const getExtensionValue = (url: string): string | undefined => {
    return activityDefinition.extension?.find((ext: Extension) => ext.url === url)?.valueString;
  };

  const backgroundColor = getExtensionValue(APPOINTMENT_COLOR_BACKGROUND_EXTENSION);
  const borderColor = getExtensionValue(APPOINTMENT_COLOR_BORDER_EXTENSION);
  const textColor = getExtensionValue(APPOINTMENT_COLOR_TEXT_EXTENSION);

  if (backgroundColor && borderColor && textColor) {
    return { backgroundColor, borderColor, textColor };
  }

  return DEFAULT_COLOR;
}
